import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard, CheckCircle, XCircle, Loader2, Save, 
  Eye, EyeOff, AlertCircle, ExternalLink, Key
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * StripeConfigPanel Component
 * Dedicated admin panel section for Stripe configuration
 */
export const StripeConfigPanel = ({ adminId }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [formData, setFormData] = useState({
    secretKey: '',
    webhookSecret: ''
  });

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/integrations/status`);
      setStatus(response.data?.stripe || {});
    } catch (error) {
      console.error('Error fetching Stripe status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.secretKey && !formData.webhookSecret) {
      toast.error(t('stripe_config.enter_key'));
      return;
    }

    setSaving(true);
    try {
      const keysToSave = {};
      if (formData.secretKey.trim()) {
        keysToSave.stripe_api_key = formData.secretKey.trim();
      }
      if (formData.webhookSecret.trim()) {
        keysToSave.stripe_webhook_secret = formData.webhookSecret.trim();
      }

      await axios.post(`${API}/admin/api-keys`, keysToSave, {
        headers: { 'X-Admin-Id': adminId }
      });

      toast.success(t('stripe_config.saved_success'));
      setFormData({ secretKey: '', webhookSecret: '' });
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('stripe_config.save_error'));
    } finally {
      setSaving(false);
    }
  };

  const isConfigured = status?.configured || false;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-2xl border border-indigo-500/30 overflow-hidden"
      data-testid="stripe-config-panel"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
            <CreditCard className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-white">Stripe</h2>
            <p className="text-indigo-100 text-sm">{t('stripe_config.subtitle')}</p>
          </div>
          <div className={`px-4 py-2 rounded-full text-sm font-medium ${
            isConfigured 
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' 
              : 'bg-red-500/20 text-red-300 border border-red-500/30'
          }`}>
            {isConfigured ? (
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {t('stripe_config.connected')}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                {t('stripe_config.not_configured')}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Info Box */}
        <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-indigo-300 font-medium mb-1">{t('stripe_config.info_title')}</p>
              <p className="text-gray-400">{t('stripe_config.info_desc')}</p>
            </div>
          </div>
        </div>

        {/* Secret Key Input */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-white font-medium">
            <Key className="w-4 h-4 text-indigo-400" />
            {t('stripe_config.secret_key')}
          </label>
          <div className="relative">
            <Input
              type={showSecretKey ? 'text' : 'password'}
              value={formData.secretKey}
              onChange={(e) => setFormData({ ...formData, secretKey: e.target.value })}
              placeholder="sk_live_..."
              className="bg-gray-800 border-gray-600 text-white pr-12 h-12 text-lg font-mono"
            />
            <button
              type="button"
              onClick={() => setShowSecretKey(!showSecretKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
            >
              {showSecretKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-gray-500 text-xs">
            {t('stripe_config.secret_key_hint')}
          </p>
        </div>

        {/* Webhook Secret Input */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-white font-medium">
            <Key className="w-4 h-4 text-purple-400" />
            {t('stripe_config.webhook_secret')} <span className="text-gray-500 text-sm">({t('stripe_config.optional')})</span>
          </label>
          <div className="relative">
            <Input
              type={showWebhookSecret ? 'text' : 'password'}
              value={formData.webhookSecret}
              onChange={(e) => setFormData({ ...formData, webhookSecret: e.target.value })}
              placeholder="whsec_..."
              className="bg-gray-800 border-gray-600 text-white pr-12 h-12 text-lg font-mono"
            />
            <button
              type="button"
              onClick={() => setShowWebhookSecret(!showWebhookSecret)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
            >
              {showWebhookSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-gray-500 text-xs">
            {t('stripe_config.webhook_hint')}
          </p>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving || (!formData.secretKey && !formData.webhookSecret)}
          className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold text-lg"
        >
          {saving ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {t('stripe_config.saving')}</>
          ) : (
            <><Save className="w-5 h-5 mr-2" /> {t('stripe_config.save_key')}</>
          )}
        </Button>

        {/* Help Section */}
        <details className="group">
          <summary className="flex items-center gap-2 cursor-pointer text-indigo-400 hover:text-indigo-300 transition-colors">
            <span className="text-sm font-medium">{t('stripe_config.how_to_get')}</span>
            <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          <div className="mt-4 pl-4 border-l-2 border-indigo-500/30 space-y-3">
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 bg-indigo-600/50 rounded-full flex items-center justify-center text-xs text-white flex-shrink-0">1</span>
              <div>
                <p className="text-white text-sm">{t('stripe_config.step_1')}</p>
                <a 
                  href="https://dashboard.stripe.com/apikeys" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-indigo-400 text-sm hover:underline inline-flex items-center gap-1"
                >
                  dashboard.stripe.com/apikeys
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 bg-indigo-600/50 rounded-full flex items-center justify-center text-xs text-white flex-shrink-0">2</span>
              <p className="text-white text-sm">{t('stripe_config.step_2')}</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 bg-indigo-600/50 rounded-full flex items-center justify-center text-xs text-white flex-shrink-0">3</span>
              <p className="text-white text-sm">{t('stripe_config.step_3')}</p>
            </div>
          </div>
        </details>

        {/* Webhook URL Info */}
        {isConfigured && (
          <div className="bg-gray-800/50 rounded-xl p-4">
            <p className="text-gray-300 text-sm font-medium mb-2">{t('stripe_config.webhook_url_label')}</p>
            <div className="bg-gray-900 rounded-lg p-3 font-mono text-sm break-all">
              <span className="text-emerald-400">{API}/subscriptions/webhook</span>
            </div>
            <p className="text-gray-500 text-xs mt-2">
              {t('stripe_config.webhook_events')}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default StripeConfigPanel;
